import { CheckCircle, ChefHat, Clock, LogOut, Moon, Printer, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PrintCardComponent } from '../components/billing/KOTDisplay';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import Footer from '../components/layout/Footer';
import ordersService from '../services/ordersService';
import { theme } from '../styles/theme';

// Hide page numbers and headers/footers in print
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @page {
      margin: 0;
      padding: 0;
      size: auto;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;
  document.head.appendChild(style);
}

const Container = styled.div`
  min-height: 100vh;
  background-color: ${({ darkMode }) => darkMode ? '#1a1a1a' : theme.colors.background};
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;
  position: relative;
  transition: background-color 0.3s ease;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
  }

  @media print {
    padding: 0;
    margin: 0;
    background-color: transparent;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${({ darkMode }) => darkMode ? '#2a2a2a' : theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  color: ${({ darkMode }) => darkMode ? '#ffffff' : theme.colors.text};
  transition: all 0.3s ease;

  @media print {
    display: none;
  }

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
  }
`;

const Controls = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  padding: ${theme.spacing.md};
  background-color: ${({ darkMode }) => darkMode ? '#2a2a2a' : theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};

  @media print {
    display: none;
  }
`;

const CategoryButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  border: 2px solid ${({ active, darkMode }) => 
    active 
      ? theme.colors.primary 
      : darkMode 
        ? '#404040' 
        : theme.colors.border};
  background-color: ${({ active, darkMode }) => 
    active 
      ? theme.colors.primary 
      : darkMode 
        ? '#1a1a1a' 
        : theme.colors.white};
  color: ${({ active, darkMode }) => 
    active 
      ? theme.colors.white 
      : darkMode 
        ? '#ffffff' 
        : theme.colors.text};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }

  @media print {
    display: none;
  }
`;

const OrderCard = styled(Card)`
  padding: ${theme.spacing.lg};
  background-color: ${({ darkMode }) => darkMode ? '#2a2a2a' : theme.colors.white};
  color: ${({ darkMode }) => darkMode ? '#ffffff' : theme.colors.text};
  border-left: 4px solid ${({ status }) => {
    if (status === 'ready') return theme.colors.status.ready;
    if (status === 'preparing') return theme.colors.status.preparing;
    return theme.colors.status.pending;
  }};
  transition: all 0.3s ease;
  animation: ${({ isNew }) => isNew ? 'pulse 0.5s ease' : 'none'};

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${({ darkMode }) => darkMode ? '#404040' : theme.colors.border};

  div {
    h3 {
      font-size: ${theme.typography.fontSize.xl};
      font-weight: ${theme.typography.fontWeight.bold};
      margin-bottom: ${theme.spacing.xs};
    }

    p {
      font-size: ${theme.typography.fontSize.sm};
      color: ${({ darkMode }) => darkMode ? '#b0b0b0' : theme.colors.textSecondary};
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xs};
    }
  }
`;

const OrderNumber = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
`;

const OrderItems = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.sm};
  background-color: ${({ darkMode }) => darkMode ? '#1a1a1a' : theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};

  &:last-child {
    margin-bottom: 0;
  }

  div {
    flex: 1;

    h4 {
      font-size: ${theme.typography.fontSize.base};
      font-weight: ${theme.typography.fontWeight.semiBold};
      margin-bottom: ${theme.spacing.xs};
    }

    p {
      font-size: ${theme.typography.fontSize.sm};
      color: ${({ darkMode }) => darkMode ? '#b0b0b0' : theme.colors.textSecondary};
      font-style: italic;
    }
  }

  span {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
  }
`;

const StatusButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.sm};
`;

const PrintButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 2px solid ${({ darkMode }) => darkMode ? '#404040' : theme.colors.border};
`;

const PrintArea = styled.div`
  display: none;

  @media print {
    display: block;
    margin: 0;
    padding: 0;
    background: white;
  }
`;

const FooterWrapper = styled.div`
  @media print {
    display: none;
  }
`;

const StatusButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};

  svg {
    width: 20px;
    height: 20px;
  }

  ${({ active, status }) => {
    if (status === 'pending') {
      return active ? `
        background-color: ${theme.colors.status.pending};
        color: ${theme.colors.white};
      ` : '';
    }
    if (status === 'preparing') {
      return active ? `
        background-color: ${theme.colors.status.preparing};
        color: ${theme.colors.white};
      ` : '';
    }
    if (status === 'ready') {
      return active ? `
        background-color: ${theme.colors.status.ready};
        color: ${theme.colors.white};
      ` : '';
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${({ darkMode }) => darkMode ? '#b0b0b0' : theme.colors.textSecondary};
  grid-column: 1 / -1;
`;

const categories = ['All', 'Starters', 'Main Course', 'Desserts', 'Beverages'];

// Mock orders data
const initialOrders = [
  {
    id: 1,
    orderNumber: 'ORD-001',
    tableNumber: 5,
    timestamp: '2:30 PM',
    status: 'pending',
    category: 'Starters',
    items: [
      { name: 'Paneer Tikka', quantity: 2, notes: 'Extra spicy' },
      { name: 'Samosa', quantity: 4 },
    ],
  },
  {
    id: 2,
    orderNumber: 'ORD-002',
    tableNumber: 12,
    timestamp: '2:45 PM',
    status: 'preparing',
    category: 'Main Course',
    items: [
      { name: 'Chicken Biryani', quantity: 2, notes: 'No onions' },
      { name: 'Butter Naan', quantity: 4 },
    ],
  },
  {
    id: 3,
    orderNumber: 'ORD-003',
    tableNumber: 8,
    timestamp: '3:00 PM',
    status: 'ready',
    category: 'Desserts',
    items: [
      { name: 'Gulab Jamun', quantity: 3 },
      { name: 'Ice Cream', quantity: 2, notes: 'Vanilla only' },
    ],
  },
];

function KitchenDisplay() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [orders, setOrders] = useState([]);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [previousOrderIds, setPreviousOrderIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [printingCard, setPrintingCard] = useState(null);
  const hasLoadedRef = useRef(false);

  const handleLogout = () => {
    navigate('/login');
  };

  // Fetch orders from backend
  useEffect(() => {
    // Initial load
    fetchOrders(false);
    // Poll for new orders every 5 seconds (background refresh, no loading indicator)
    const interval = setInterval(() => {
      fetchOrders(true);
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async (isBackgroundRefresh = false) => {
    // Track if we set loading in this call
    let didSetLoading = false;
    try {
      // Only show loading on initial load, not on background refreshes
      if (!isBackgroundRefresh && !hasLoadedRef.current) {
        setLoading(true);
        didSetLoading = true;
      }
      setError(null);
      console.log('🔍 Kitchen: Fetching orders...');
      const allOrders = await ordersService.getAll();
      console.log('✅ Kitchen: Received orders:', allOrders);
      // Debug: Log first order to check table data
      if (allOrders.length > 0) {
        console.log('🔍 Kitchen: First order table data:', {
          tableNumber: allOrders[0].tableNumber,
          tableLabel: allOrders[0].tableLabel,
          section: allOrders[0].section
        });
      }
      
      if (!Array.isArray(allOrders)) {
        console.warn('⚠️ Kitchen: API did not return an array:', allOrders);
        setOrders([]);
        if (didSetLoading) {
          setLoading(false);
        }
        return;
      }
      
      // Filter only pending and preparing orders for kitchen
      const kitchenOrders = allOrders
        .filter(order => {
          const status = order.status?.toLowerCase();
          return status === 'pending' || status === 'preparing';
        })
        .map(order => {
          // Preserve table display info
          const tableInfo = {
            id: order.id,
            orderNumber: order.orderNumber,
            tableNumber: order.tableNumber,
            tableLabel: order.tableLabel,
            section: order.section,
            timestamp: new Date(order.createdAt || order.created_at || Date.now()).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            status: order.status,
            items: (order.items || []).map(item => ({
              name: item.itemName || item.name,
              quantity: item.quantity || item.qty || 1,
              notes: item.specialInstructions || ''
            })),
            category: 'All', // Default category
            createdAt: order.createdAt || order.created_at
          };
          // Debug log for each order's table info
          console.log(`📝 Kitchen: Order ${order.orderNumber} - Table: ${order.tableLabel || order.section || 'N/A'} ${order.tableNumber}`);
          return tableInfo;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Newest first

      console.log('🍳 Kitchen: Filtered orders:', kitchenOrders.length, 'orders');

      // Detect new orders for animation
      const currentIds = new Set(kitchenOrders.map(o => o.id));
      const newIds = new Set([...currentIds].filter(id => !previousOrderIds.has(id)));
      
      if (newIds.size > 0) {
        console.log('🆕 Kitchen: New orders detected:', newIds.size);
        setNewOrderIds(newIds);
        // Remove animation after 2 seconds
        setTimeout(() => {
          setNewOrderIds(new Set());
        }, 2000);
      }

      setPreviousOrderIds(currentIds);
      // Smooth merge: preserve existing orders while updating data
      setOrders((prevOrders) => {
        if (prevOrders.length === 0 || kitchenOrders.length === 0) {
          return kitchenOrders;
        }
        // Create a map of existing orders for quick lookup
        const existingMap = new Map(prevOrders.map(o => [o.id, o]));
        // Merge new data with existing to prevent flash
        return kitchenOrders.map(newOrder => {
          const existing = existingMap.get(newOrder.id);
          if (existing) {
            // Preserve the order instance to prevent React unmount/remount
            return { ...existing, ...newOrder };
          }
          return newOrder;
        });
      });
      console.log('✅ Kitchen: Orders updated smoothly, total:', kitchenOrders.length);
      // Mark that we've loaded data at least once
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('❌ Kitchen: Error fetching orders:', error);
      setError(error.message || 'Failed to load orders. Make sure backend is running.');
    } finally {
      // Only clear loading if we set it
      if (didSetLoading) {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      // Map kitchen status to backend status
      const statusMap = {
        'pending': 'pending',
        'preparing': 'preparing',
        'ready': 'preparing' // When ready, keep as preparing for counter to see
      };

      const backendStatus = statusMap[newStatus] || newStatus;
      
      // Update order status in backend
      await ordersService.update(orderId, { status: backendStatus });
      
      // Update local state
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: backendStatus } : order
        )
      );

      // If marked as ready, update status to 'preparing' (so counter can see it)
      // In a real system, you might want a separate 'ready' status
      if (newStatus === 'ready') {
        // Order is ready - will appear in counter
        alert(`Order ${orderId} is ready! Sent to counter.`);
      }
      
      // Refresh orders (background refresh, no loading indicator)
      await fetchOrders(true);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(`Failed to update order status: ${error.message}`);
    }
  };

  // Handle print kitchen order card
  const handlePrintKOT = (order) => {
    setPrintingCard(order);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Filter orders by status for kitchen display
  const filteredOrders = orders.filter(order => {
    if (selectedCategory === 'All') return true;
    // For now, show all orders regardless of category
    // You can add category filtering later if needed
    return true;
  });

  return (
    <Container darkMode={darkMode}>
      <Header darkMode={darkMode}>
        <h1>
          <ChefHat size={32} />
          Kitchen Display System
        </h1>
        <Controls>
          <Button
            variant={darkMode ? 'secondary' : 'outline'}
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            Logout
          </Button>
        </Controls>
      </Header>

      <CategoryFilter darkMode={darkMode}>
        {categories.map((category) => (
          <CategoryButton
            key={category}
            active={selectedCategory === category}
            darkMode={darkMode}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </CategoryButton>
        ))}
      </CategoryFilter>

      <OrdersGrid>
        {loading ? (
          <EmptyState darkMode={darkMode}>
            <p>Loading orders...</p>
          </EmptyState>
        ) : error ? (
          <EmptyState darkMode={darkMode}>
            <p style={{ color: '#ff4444' }}>Error: {error}</p>
            <Button variant="primary" onClick={fetchOrders} style={{ marginTop: theme.spacing.md }}>
              Retry
            </Button>
          </EmptyState>
        ) : filteredOrders.length === 0 ? (
          <EmptyState darkMode={darkMode}>
            <p>No orders in kitchen. Waiting for new orders...</p>
          </EmptyState>
        ) : (
          filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              darkMode={darkMode}
              status={order.status}
              isNew={newOrderIds.has(order.id)}
            >
              <OrderHeader darkMode={darkMode}>
                <div>
                  <h3>{
                    (() => {
                      const label = order.tableLabel;
                      const section = order.section;
                      const num = order.tableNumber;
                      // Debug logging
                      console.log(`🖥️ Kitchen Display: Rendering order ${order.orderNumber} - label='${label}', section='${section}', num='${num}'`);
                      if (label && label.trim()) return label;
                      if (section && section.trim()) return `${section} ${num}`;
                      return `Table ${num}`;
                    })()
                  }</h3>
                  <p>
                    <Clock size={14} />
                    {order.timestamp} • {order.orderNumber}
                  </p>
                </div>
                <OrderNumber>{order.orderNumber}</OrderNumber>
              </OrderHeader>

              <OrderItems>
                {order.items.map((item, index) => (
                  <OrderItem key={index} darkMode={darkMode}>
                    <div>
                      <h4>{item.quantity}x {item.name}</h4>
                      {item.notes && <p>Note: {item.notes}</p>}
                    </div>
                  </OrderItem>
                ))}
              </OrderItems>

              <StatusButtons>
                <StatusButton
                  variant={order.status === 'pending' ? 'primary' : 'secondary'}
                  status="pending"
                  active={order.status === 'pending'}
                  onClick={() => handleStatusChange(order.id, 'pending')}
                >
                  <Clock />
                  Pending
                </StatusButton>
                <StatusButton
                  variant={order.status === 'preparing' ? 'primary' : 'secondary'}
                  status="preparing"
                  active={order.status === 'preparing'}
                  onClick={() => handleStatusChange(order.id, 'preparing')}
                >
                  <ChefHat />
                  Start Cooking
                </StatusButton>
                <StatusButton
                  variant="primary"
                  status="ready"
                  onClick={() => handleStatusChange(order.id, 'ready')}
                >
                  <CheckCircle />
                  Mark Ready
                </StatusButton>
              </StatusButtons>

              <PrintButtons darkMode={darkMode}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePrintKOT(order)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', gridColumn: '1 / -1' }}
                >
                  <Printer size={16} />
                  Print Kitchen Order
                </Button>
              </PrintButtons>
            </OrderCard>
          ))
        )}
      </OrdersGrid>
      
      <PrintArea>
        {printingCard && (
          <PrintCardComponent order={printingCard} />
        )}
      </PrintArea>

      <FooterWrapper>
        <Footer hasSidebar={false} />
      </FooterWrapper>
    </Container>
  );
}

export default KitchenDisplay;

